"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState, useEffect } from "react";
import { SenseiMark } from "./SenseiMark";
import { SolutionsMenu } from "./SolutionsMenu";
import { ResourcesMenu } from "./ResourcesMenu";
import { useAuthContext } from "@/platform/auth/AuthContext";

interface NavbarProps {
  /** Override active link — useful for server-rendered wrappers that pass the route down */
  activeHref?: string;
}

export function Navbar({ activeHref }: NavbarProps) {
  const pathname = usePathname();
  // null = not yet initialised (avoids a false "dark" flash before localStorage is read)
  const [light, setLight] = useState<boolean | null>(null);
  const { user, profile, loading, signOut } = useAuthContext();

  // Initialise from localStorage on mount — default to light for new users
  useEffect(() => {
    const stored = localStorage.getItem("theme");
    const isLight = stored === null ? true : stored === "light";
    setLight(isLight);
    document.documentElement.classList.toggle("theme-light", isLight);
  }, []);

  // Sync class + localStorage whenever the user explicitly toggles
  useEffect(() => {
    // Skip until initialised — prevents wiping theme-light before localStorage is read
    if (light === null) return;
    document.documentElement.classList.toggle("theme-light", light);
    localStorage.setItem("theme", light ? "light" : "dark");
  }, [light]);
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<"solutions" | "resources" | null>(null);

  const toggleMenu = (menu: "solutions" | "resources") =>
    setOpenMenu((v) => (v === menu ? null : menu));

  // Lock body scroll when mobile menu is open (Safari iOS fix)
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
  }, [mobileMenuOpen]);

  // Determine active link: prefer prop, fall back to pathname matching
  const activePath = activeHref ?? pathname;

  return (
    <nav className="nav">
      <div className="nav-inner">
        <Link href="/" className="nav-brand">
          <SenseiMark size={40} />
          <span className="wordmark">sensei<span className="dot">.</span></span>
        </Link>

        <div className="nav-links">
          <Link
            href="/ask"
            className={`nav-link${activePath.startsWith("/ask") ? " active" : ""}`}
            aria-current={activePath.startsWith("/ask") ? "page" : undefined}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            Ask
          </Link>
          <Link
            href="/draft"
            className={`nav-link${activePath.startsWith("/draft") ? " active" : ""}`}
            aria-current={activePath.startsWith("/draft") ? "page" : undefined}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="8" y1="13" x2="16" y2="13"/>
              <line x1="8" y1="17" x2="13" y2="17"/>
            </svg>
            Draft
          </Link>
          <Link
            href="/diagnostics"
            className={`nav-link${activePath.startsWith("/diagnostics") ? " active" : ""}`}
            aria-current={activePath.startsWith("/diagnostics") ? "page" : undefined}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
            Diagnostics
          </Link>
          <Link
            href="/agentic-workflows"
            className={`nav-link${activePath.startsWith("/agentic-workflows") ? " active" : ""}`}
            aria-current={activePath.startsWith("/agentic-workflows") ? "page" : undefined}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 2l2.4 6.4L21 9l-5 4.4L17.5 20 12 16.7 6.5 20 8 13.4 3 9l6.6-.6z"/>
            </svg>
            Agentic Workflows
          </Link>
          <Link
            href="/analyse"
            className={`nav-link${activePath.startsWith("/analyse") ? " active" : ""}`}
            aria-current={activePath.startsWith("/analyse") ? "page" : undefined}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="20" x2="18" y2="10"/>
              <line x1="12" y1="20" x2="12" y2="4"/>
              <line x1="6" y1="20" x2="6" y2="14"/>
            </svg>
            Analyse
          </Link>
          <SolutionsMenu isOpen={openMenu === "solutions"} onToggle={() => toggleMenu("solutions")} />
          <ResourcesMenu isOpen={openMenu === "resources"} onToggle={() => toggleMenu("resources")} />
          <Link
            href="/enterprise-security"
            className={`nav-link${activePath.startsWith("/enterprise-security") ? " active" : ""}`}
            aria-current={activePath.startsWith("/enterprise-security") ? "page" : undefined}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            Enterprise Security
          </Link>
        </div>

        <div className="nav-spacer" />

        <button
          className="theme-toggle"
          aria-label="Toggle theme"
          onClick={() => setLight((v) => !v)}
        >
          {light ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
            </svg>
          )}
        </button>

        <div className="nav-cta">
          <Link
            href="/pricing"
            className={`pricing-btn${activePath === "/pricing" ? " active" : ""}`}
          >
            Pricing
          </Link>
          {loading ? null : user ? (
            <div className="nav-avatar-wrap">
              <button
                type="button"
                className="nav-avatar-btn"
                aria-label="Account menu"
                aria-expanded={avatarMenuOpen}
                onClick={() => setAvatarMenuOpen((v) => !v)}
              >
                {profile?.photoURL ? (
                  // eslint-disable-next-line @next/next/no-img-element -- external auth avatar (unknown OAuth provider origin); next/image would need remotePatterns for each
                  <img src={profile.photoURL} alt="" width={32} height={32} className="nav-avatar-img" />
                ) : (
                  <span className="nav-avatar-initial">
                    {(profile?.displayName ?? profile?.email ?? "U").charAt(0).toUpperCase()}
                  </span>
                )}
                {(profile?.subscription?.plan === "pro_monthly" || profile?.subscription?.plan === "pro_annual" || profile?.subscription?.plan === "enterprise") && (
                  <span className="nav-avatar-pro-badge" aria-label="Pro plan" title="You're on a Pro plan">
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="#FFD700" aria-hidden="true">
                      <path d="M12 2L15 8L22 9L17 14L18.5 21L12 17.5L5.5 21L7 14L2 9L9 8L12 2Z"/>
                    </svg>
                  </span>
                )}
              </button>
              {avatarMenuOpen && (
                <>
                  <div className="nav-avatar-backdrop" onClick={() => setAvatarMenuOpen(false)} aria-hidden="true" />
                  <div className="nav-avatar-menu" role="menu">
                    <div className="nav-avatar-info">
                      <p className="nav-avatar-name">{profile?.displayName ?? "Account"}</p>
                      <p className="nav-avatar-email">{profile?.email ?? ""}</p>
                    </div>
                    <div className="nav-avatar-divider" />
                    <Link href="/settings" className="nav-avatar-item" role="menuitem" onClick={() => setAvatarMenuOpen(false)}>
                      Settings
                    </Link>
                    <button
                      type="button"
                      className="nav-avatar-item nav-avatar-item--signout"
                      role="menuitem"
                      onClick={() => { setAvatarMenuOpen(false); signOut(); }}
                    >
                      Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <Link href={`/login?redirect_to=${encodeURIComponent(pathname)}`} className="btn btn-primary btn-sm">Sign in</Link>
          )}
        </div>

        {/* Hamburger — only visible below 1024px */}
        <button
          type="button"
          className={`nav-hamburger${mobileMenuOpen ? " is-open" : ""}`}
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-menu"
          onClick={() => setMobileMenuOpen((v) => !v)}
        >
          <span className="hamburger-bar" />
          <span className="hamburger-bar" />
          <span className="hamburger-bar" />
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileMenuOpen && (
        <>
          <div
            className="mobile-menu-backdrop"
            aria-hidden="true"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div id="mobile-menu" className="mobile-menu" role="dialog" aria-label="Navigation menu">
            <button
              type="button"
              className="mobile-menu-close"
              aria-label="Close menu"
              onClick={() => setMobileMenuOpen(false)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
            <Link
              href="/ask"
              className={`nav-link${activePath.startsWith("/ask") ? " active" : ""}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              Ask
            </Link>
            <Link
              href="/draft"
              className={`nav-link${activePath.startsWith("/draft") ? " active" : ""}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="8" y1="13" x2="16" y2="13"/>
                <line x1="8" y1="17" x2="13" y2="17"/>
              </svg>
              Draft
            </Link>
            <Link
              href="/diagnostics"
              className={`nav-link${activePath.startsWith("/diagnostics") ? " active" : ""}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
              </svg>
              Diagnostics
            </Link>
            <Link
              href="/agentic-workflows"
              className={`nav-link${activePath.startsWith("/agentic-workflows") ? " active" : ""}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 2l2.4 6.4L21 9l-5 4.4L17.5 20 12 16.7 6.5 20 8 13.4 3 9l6.6-.6z"/>
              </svg>
              Agentic Workflows
            </Link>
            <Link
              href="/analyse"
              className={`nav-link${activePath.startsWith("/analyse") ? " active" : ""}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="18" y1="20" x2="18" y2="10"/>
                <line x1="12" y1="20" x2="12" y2="4"/>
                <line x1="6" y1="20" x2="6" y2="14"/>
              </svg>
              Analyse
            </Link>
            <SolutionsMenu mobile onMobileClose={() => setMobileMenuOpen(false)} />
            <ResourcesMenu mobile onMobileClose={() => setMobileMenuOpen(false)} />
            <Link
              href="/enterprise-security"
              className={`nav-link${activePath.startsWith("/enterprise-security") ? " active" : ""}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              Enterprise
            </Link>
            <Link
              href="/pricing"
              className={`nav-link${activePath === "/pricing" ? " active" : ""}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="12" y1="1" x2="12" y2="23"/>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
              Pricing
            </Link>
            {!loading && !user && (
              <Link href={`/login?redirect_to=${encodeURIComponent(pathname)}`} className="btn btn-primary btn-sm" onClick={() => setMobileMenuOpen(false)}>
                Sign in
              </Link>
            )}
          </div>
        </>
      )}
    </nav>
  );
}
